import { type JSX, useMemo } from 'react';
import { generatePath } from 'react-router';

import { DocumentApi } from 'Api/Document/DocumentApi';
import { Button, Link } from 'Components/Buttons/Buttons';
import { CircleIndicatorControlEffectiveness } from 'Components/CircleIndicators/CircleIndicatorControlEffectiveness';
import { CircleIndicatorRiskRating } from 'Components/CircleIndicators/CircleIndicatorRiskRating';
import { ColumnConfig, ColumnType } from 'Components/Table/Columns';
import { Table } from 'Components/Table/Table';
import { Text } from 'Components/Text/Text';
import { TPRM_ASSESSMENT_HISTORY_IRQ, TPRM_FINAL_REVIEW_HISTORICAL, UCRM_ASSESSMENT_HISTORY_IRQ, UCRM_FINAL_REVIEW_HISTORICAL } from 'Config/PathTemplates';
import { DueDiligenceWorkflowFileWithSource, categorizeDueDiligenceWorkflowFiles } from 'Models/DueDiligenceWorkflow';
import { Effectiveness } from 'Models/OperationalControls';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';
import { TprmOrUcrmDueDiligenceWorkflowArchive, getArchivedAssessmentSubject } from 'Models/UcrmTprm';

export interface ArchivedDueDiligenceWorkflowsTableProps<T extends TprmOrUcrmDueDiligenceWorkflowArchive> {
    documentApi: DocumentApi;
    module: DueDiligenceModule;
    onAssessmentsClicked: (workflowArchive: T) => void;
    onFilesClicked: (workflowArchive: T, categorizedFiles: DueDiligenceWorkflowFileWithSource[]) => void;
    workflowArchives: T[];
}

/**
 * Renders a table of an assessment subject's previous due diligence workflows (workflow archives), where the user can sort by the workflows' completion dates.
 * Used on the TPRM Service Dashboard and the UCRM Use Case Dashboard to provide the user with insight into historical due diligence data. (The user can also view current due diligence data there.)
 *
 * Includes links to the following locations to display detailed historical data:
 * * Inherent Risk Questionnaire
 * * Due Diligence Questionnaires/Assessments
 * * Final Review (PDF)
 * * Due Diligence Workflow files (modal displaying all files from a single due diligence workflow, categorized by source)
 */
export const ArchivedDueDiligenceWorkflowsTable = <T extends TprmOrUcrmDueDiligenceWorkflowArchive>(props: ArchivedDueDiligenceWorkflowsTableProps<T>): JSX.Element => {
    // Memoize the relatively expensive calculation of iterating through each entire workflow archive so that it does not happen whenever the parent's state changes.
    const getFilesForWorkflowArchive: (workflowArchive: T) => DueDiligenceWorkflowFileWithSource[] = useMemo(() => {
        const filesByWorkflowArchiveTimestamp = new Map<string, DueDiligenceWorkflowFileWithSource[]>();
        props.workflowArchives.forEach((workflowArchive) => {
            filesByWorkflowArchiveTimestamp.set(workflowArchive.timestamp, categorizeDueDiligenceWorkflowFiles(workflowArchive, getArchivedAssessmentSubject(props.module, workflowArchive)));
        });
        return (workflowArchive: T) => filesByWorkflowArchiveTimestamp.get(workflowArchive.timestamp) ?? [];
    }, [props.module, props.workflowArchives]);

    const columns: ColumnConfig<T>[] = [
        {
            type: ColumnType.DATE,
            name: 'Date',
            getDateString: (workflowArchive) => workflowArchive.timestamp,
        },
        {
            type: ColumnType.CUSTOM,
            name: 'Inherent Risk',
            getValue: (workflowArchive) => getArchivedAssessmentSubject(props.module, workflowArchive).assessment_workflow_data.irq_inherent_risk_score,
            getTableCellContent: (workflowArchive) => (
                <>
                    <CircleIndicatorRiskRating pageVariant="dashboard" riskRating={getArchivedAssessmentSubject(props.module, workflowArchive).assessment_workflow_data.irq_inherent_risk_score} />
                    <Link to={generatePath(props.module === ProductModule.UCRM ? UCRM_ASSESSMENT_HISTORY_IRQ : TPRM_ASSESSMENT_HISTORY_IRQ, { third_party_id: getArchivedAssessmentSubject(props.module, workflowArchive).vendor_id, service_id: getArchivedAssessmentSubject(props.module, workflowArchive).id, timestamp: workflowArchive.timestamp })} size="sm">
                        View Inherent Risk Questionnaire
                    </Link>
                </>
            ),
        },
        {
            type: ColumnType.CUSTOM,
            name: 'Control Effectiveness',
            getValue: (workflowArchive) => getArchivedAssessmentSubject(props.module, workflowArchive).assessment_workflow_data.final_review_human_assessment.effectiveness ?? Effectiveness.UNRATED,
            getTableCellContent: (workflowArchive) => (
                <>
                    <CircleIndicatorControlEffectiveness pageVariant="dashboard" effectiveness={getArchivedAssessmentSubject(props.module, workflowArchive).assessment_workflow_data.final_review_human_assessment.effectiveness} />
                    <Button variant="linkText" size="sm" onClick={() => props.onAssessmentsClicked(workflowArchive)}>
                        View Assessments
                    </Button>
                </>
            ),
        },
        {
            type: ColumnType.CUSTOM,
            name: 'Residual Risk',
            getValue: (workflowArchive) => getArchivedAssessmentSubject(props.module, workflowArchive).assessment_workflow_data.final_review_human_assessment.residual_risk,
            getTableCellContent: (workflowArchive) => (
                <>
                    <CircleIndicatorRiskRating pageVariant="dashboard" riskRating={getArchivedAssessmentSubject(props.module, workflowArchive).assessment_workflow_data.final_review_human_assessment.residual_risk} />
                    <Link to={generatePath(props.module === ProductModule.UCRM ? UCRM_FINAL_REVIEW_HISTORICAL : TPRM_FINAL_REVIEW_HISTORICAL, { third_party_id: getArchivedAssessmentSubject(props.module, workflowArchive).vendor_id, service_id: getArchivedAssessmentSubject(props.module, workflowArchive).id, timestamp: workflowArchive.timestamp })} size="sm">
                        View Final Review
                    </Link>
                </>
            ),
        },
        {
            // Normally this would be a `ColumnType.STRING`, but custom layout is applied here for the sake of visual balance: each column shows a string and a link/button.
            type: ColumnType.CUSTOM,
            name: 'Files',
            getValue: (workflowArchive) => getFilesForWorkflowArchive(workflowArchive).length,
            getTableCellContent: (workflowArchive) => {
                const files = getFilesForWorkflowArchive(workflowArchive);
                return (
                    <>
                        <Text noStyles color="white">{`${files.length} ${files.length === 1 ? 'file' : 'files'}`}</Text>
                        <Button variant="linkText" size="sm" onClick={() => props.onFilesClicked(workflowArchive, files)}>
                            View Files
                        </Button>
                    </>
                );
            },
        },
    ];

    return <Table<T> columns={columns} defaultSortColumn="Date" defaultSortDirection="descending" disableSorting filters={[]} getUniqueIdForRow={(workflowArchive) => workflowArchive.timestamp} rowsData={props.workflowArchives} pageVariant="dashboard" />;
};
